'use client';

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import type { SLTPState, TradeMarker } from './TradingDashboardInteractive';

interface ChartPanelProps {
  symbol: string;
  activeTimeframe?: string;
  onTimeframeChange?: (tf: string) => void;
  sltp?: SLTPState;
  tradeMarkers?: TradeMarker[];
  currentPrice?: number;
  onCancelSL?: () => void;
  onCancelTP?: () => void;
}

const TIMEFRAMES = ['1m', '5m', '15m', '1h', '4h', 'D', 'W'];
const INDICATORS = ['MA', 'EMA', 'MACD', 'RSI', 'BB'];

// Map UI timeframe to TradingView interval
function toTVInterval(tf: string): string {
  const map: Record<string, string> = {
    '1m': '1', '5m': '5', '15m': '15', '1h': '60', '4h': '240', 'D': 'D', 'W': 'W',
  };
  return map[tf] ?? '60';
}

// Compute a vertical position % for a price level relative to visible price range
function priceToPercent(price: number, currentPrice: number): number {
  // Show ±10% range around current price
  const range = currentPrice * 0.10;
  const top = currentPrice + range;
  const bottom = currentPrice - range;
  const pct = ((top - price) / (top - bottom)) * 100;
  return Math.max(2, Math.min(96, pct));
}

export default function ChartPanel({
  symbol,
  activeTimeframe: externalTf,
  onTimeframeChange,
  sltp,
  tradeMarkers = [],
  currentPrice = 0,
  onCancelSL,
  onCancelTP,
}: ChartPanelProps) {
  const [internalTf, setInternalTf] = useState('1h');
  const [activeIndicators, setActiveIndicators] = useState<string[]>(['MA']);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isHydrated, setIsHydrated] = useState(false);

  const activeTimeframe = externalTf ?? internalTf;

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleTimeframeChange = (tf: string) => {
    if (onTimeframeChange) {
      onTimeframeChange(tf);
    } else {
      setInternalTf(tf);
    }
  };

  const toggleIndicator = (ind: string) => {
    setActiveIndicators(prev =>
      prev.includes(ind) ? prev.filter(i => i !== ind) : [...prev, ind]
    );
  };

  const tvSymbol = symbol.replace('/', '');
  const tvInterval = toTVInterval(activeTimeframe);

  const slPrice = sltp?.slEnabled && sltp.slPrice ? parseFloat(sltp.slPrice) : null;
  const tpPrice = sltp?.tpEnabled && sltp.tpPrice ? parseFloat(sltp.tpPrice) : null;
  const hasOverlay = (slPrice !== null || tpPrice !== null || tradeMarkers.length > 0) && currentPrice > 0;

  return (
    <div className="flex flex-col h-full overflow-hidden" style={{ background: '#141414' }}>
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#2a2a2a] flex-wrap gap-2" style={{ background: '#141414' }}>
        {/* Timeframes */}
        <div className="flex items-center gap-0.5">
          {TIMEFRAMES.map(tf => (
            <button
              key={tf}
              onClick={() => handleTimeframeChange(tf)}
              className={`px-2.5 py-1 text-xs font-mono font-medium transition-all duration-150 ${
                activeTimeframe === tf
                  ? 'bg-[rgba(76,110,244,0.2)] text-[#4c6ef4] border border-[rgba(76,110,244,0.4)]'
                  : 'text-[#94A3B8] hover:text-[#F1F5F9] hover:bg-[#242424]'
              }`}
            >
              {tf}
            </button>
          ))}
        </div>

        {/* Indicators */}
        <div className="flex items-center gap-1">
          <span className="text-[10px] text-[#94A3B8] uppercase tracking-wider mr-1">Indicators:</span>
          {INDICATORS.map(ind => (
            <button
              key={ind}
              onClick={() => toggleIndicator(ind)}
              className={`px-2 py-1 text-[10px] font-mono transition-all duration-150 ${
                activeIndicators.includes(ind)
                  ? 'bg-[rgba(76,110,244,0.2)] text-[#4c6ef4] border border-[rgba(76,110,244,0.4)]'
                  : 'text-[#94A3B8] hover:text-[#F1F5F9] border border-transparent hover:border-[#2a2a2a]'
              }`}
            >
              {ind}
            </button>
          ))}
          <button className="ml-1 p-1 text-[#94A3B8] hover:text-[#F1F5F9] hover:bg-[#242424] transition-all">
            <Icon name="PencilSquareIcon" size={14} />
          </button>
          <button className="p-1 text-[#94A3B8] hover:text-[#F1F5F9] hover:bg-[#242424] transition-all">
            <Icon name="CameraIcon" size={14} />
          </button>
        </div>
      </div>

      {/* Chart + Overlay */}
      <div className="flex-1 relative min-h-0" ref={containerRef} style={{ background: '#141414' }}>
        {isHydrated ? (
          <iframe
            key={`${tvSymbol}-${activeTimeframe}`}
            src={`https://s.tradingview.com/widgetembed/?frameElementId=tradingview_chart&symbol=BINANCE:${tvSymbol}&interval=${tvInterval}&theme=dark&style=1&locale=en&toolbar_bg=%23141414&enable_publishing=0&hide_side_toolbar=0&allow_symbol_change=0&save_image=0&backgroundColor=%23141414&gridColor=%23141414&hide_top_toolbar=0&withdateranges=1`}
            className="w-full h-full border-0"
            style={{ background: '#141414', display: 'block' }}
            title={`TradingView chart for ${symbol}`}
            allowFullScreen
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center" style={{ background: '#141414' }}>
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-2 border-[#4c6ef4] border-t-transparent animate-spin" />
              <span className="text-sm text-[#94A3B8]">Loading chart...</span>
            </div>
          </div>
        )}

        {/* SL/TP + Trade Marker Overlay */}
        {hasOverlay && isHydrated && (
          <div className="absolute inset-0 pointer-events-none" style={{ zIndex: 10 }}>

            {/* Stop Loss line */}
            {slPrice !== null && (
              <div
                className="absolute left-0 right-0 flex items-center pointer-events-auto"
                style={{ top: `${priceToPercent(slPrice, currentPrice)}%`, transform: 'translateY(-50%)' }}
              >
                {/* Dashed line */}
                <div
                  className="flex-1"
                  style={{
                    height: '1px',
                    background: 'repeating-linear-gradient(to right, #EF4444 0px, #EF4444 8px, transparent 8px, transparent 14px)',
                  }}
                />
                {/* Label */}
                <div
                  className="flex items-center gap-1 px-2 py-0.5 ml-1 shrink-0"
                  style={{ background: '#EF4444', borderRadius: 0 }}
                >
                  <span className="text-[10px] font-bold text-white font-mono">SL ${slPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  <button
                    onClick={onCancelSL}
                    className="text-white hover:text-red-200 transition-colors ml-0.5"
                    title="Cancel Stop Loss"
                  >
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M2 2L8 8M8 2L2 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Take Profit line */}
            {tpPrice !== null && (
              <div
                className="absolute left-0 right-0 flex items-center pointer-events-auto"
                style={{ top: `${priceToPercent(tpPrice, currentPrice)}%`, transform: 'translateY(-50%)' }}
              >
                {/* Dashed line */}
                <div
                  className="flex-1"
                  style={{
                    height: '1px',
                    background: 'repeating-linear-gradient(to right, #22C55E 0px, #22C55E 8px, transparent 8px, transparent 14px)',
                  }}
                />
                {/* Label */}
                <div
                  className="flex items-center gap-1 px-2 py-0.5 ml-1 shrink-0"
                  style={{ background: '#22C55E', borderRadius: 0 }}
                >
                  <span className="text-[10px] font-bold text-white font-mono">TP ${tpPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  <button
                    onClick={onCancelTP}
                    className="text-white hover:text-green-200 transition-colors ml-0.5"
                    title="Cancel Take Profit"
                  >
                    <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                      <path d="M2 2L8 8M8 2L2 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Trade Entry Markers */}
            {tradeMarkers.map((marker) => {
              const topPct = priceToPercent(marker.price, currentPrice);
              const isBuy = marker.side === 'Buy';
              return (
                <div
                  key={marker.id}
                  className="absolute pointer-events-auto group"
                  style={{
                    top: `${topPct}%`,
                    left: '60px',
                    transform: 'translateY(-50%)',
                  }}
                >
                  {/* Triangle marker */}
                  <div className="relative flex items-center justify-center" style={{ width: 20, height: 20 }}>
                    {isBuy ? (
                      // Up-pointing triangle for Buy
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <polygon points="9,2 17,16 1,16" fill="#22C55E" stroke="#141414" strokeWidth="1"/>
                      </svg>
                    ) : (
                      // Down-pointing triangle for Sell
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <polygon points="9,16 17,2 1,2" fill="#EF4444" stroke="#141414" strokeWidth="1"/>
                      </svg>
                    )}

                    {/* Tooltip on hover */}
                    <div
                      className="absolute left-6 top-1/2 -translate-y-1/2 hidden group-hover:flex flex-col gap-0.5 px-2 py-1.5 text-[10px] font-mono whitespace-nowrap z-20"
                      style={{ background: '#1e1e1e', border: `1px solid ${isBuy ? '#22C55E' : '#EF4444'}`, minWidth: 120 }}
                    >
                      <span style={{ color: isBuy ? '#22C55E' : '#EF4444' }} className="font-bold">{isBuy ? '▲ BUY' : '▼ SELL'}</span>
                      <span className="text-[#F1F5F9]">Price: ${marker.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      <span className="text-[#94A3B8]">Size: {marker.size} {marker.symbol.split('/')[0]}</span>
                      <span className="text-[#94A3B8]">{new Date(marker.timestamp).toLocaleTimeString()}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}